import i18next from "i18next"
import {initReactI18next} from "react-i18next"

import en from "./locales/en"

/**
 * UI localization for the Agenta apps, modeled on LibreChat's `client/src/locales/i18n.ts`
 * (English bundled, other locales lazy-loaded, alias table) and Open WebUI's `src/lib/i18n`
 * (`<html lang>` follows the active language).
 */
export const supportedLocales = ["en", "zh-TW"] as const
export type SupportedLocale = (typeof supportedLocales)[number]
/** What the user picked; `auto` follows the browser. */
export type LanguagePreference = SupportedLocale | "auto"
export type TranslationResource = Record<string, string>

export const LANGUAGE_STORAGE_KEY = "agenta.lang"
export const LANGUAGE_COOKIE = "agenta-lang"

const localeLoaders: Record<
    Exclude<SupportedLocale, "en">,
    () => Promise<{default: TranslationResource}>
> = {
    "zh-TW": () => import("./locales/zh-TW"),
}

/** Lower-cased BCP 47 tags that resolve to a supported locale (LibreChat `localeAliases`). */
const localeAliases: Record<string, SupportedLocale> = {
    en: "en",
    "zh-tw": "zh-TW",
    "zh-hant": "zh-TW",
    "zh-hant-tw": "zh-TW",
    "zh-hk": "zh-TW",
    "zh-hant-hk": "zh-TW",
    "zh-mo": "zh-TW",
    "zh-hant-mo": "zh-TW",
}

export const i18n = i18next.createInstance()

void i18n.use(initReactI18next).init({
    resources: {en: {translation: en}},
    lng: "en",
    fallbackLng: "en",
    // Flat `com_*` keys: no nesting, no namespaces in the key.
    keySeparator: false,
    nsSeparator: false,
    // An empty catalog value falls back to English instead of rendering blank.
    returnEmptyString: false,
    returnNull: false,
    interpolation: {escapeValue: false},
    initAsync: false,
    react: {useSuspense: false},
})

const loadedLocales = new Set<SupportedLocale>(["en"])

export function normalizeLocale(locale?: string | null): SupportedLocale {
    if (!locale) return "en"
    const lower = locale.trim().toLowerCase()
    if (localeAliases[lower]) return localeAliases[lower]
    // `en-GB`, `en-US`… are English; any other unsupported language falls back to English too.
    return "en"
}

function browserLocale(): SupportedLocale {
    if (typeof navigator === "undefined") return "en"
    const candidates = navigator.languages?.length ? navigator.languages : [navigator.language]
    for (const candidate of candidates) {
        const locale = normalizeLocale(candidate)
        if (locale !== "en" || candidate?.toLowerCase().startsWith("en")) return locale
    }
    return "en"
}

export function resolveLocale(preference?: LanguagePreference | string | null): SupportedLocale {
    if (!preference || preference === "auto") return browserLocale()
    return normalizeLocale(preference)
}

export function readLanguagePreference(): LanguagePreference {
    try {
        const raw =
            typeof localStorage === "undefined" ? null : localStorage.getItem(LANGUAGE_STORAGE_KEY)
        if (raw === "auto") return "auto"
        if (raw && (supportedLocales as readonly string[]).includes(raw))
            return raw as SupportedLocale
    } catch {
        // Storage can be blocked (private mode); fall through to the browser language.
    }
    return "auto"
}

function writeLanguagePreference(preference: LanguagePreference) {
    try {
        localStorage.setItem(LANGUAGE_STORAGE_KEY, preference)
    } catch {
        // Not persisted; the choice still applies to this page.
    }
    if (typeof document !== "undefined") {
        document.cookie = `${LANGUAGE_COOKIE}=${preference}; path=/; max-age=31536000; SameSite=Lax`
    }
}

export async function ensureLocale(locale: SupportedLocale) {
    if (loadedLocales.has(locale) || locale === "en") return
    const {default: resource} = await localeLoaders[locale]()
    i18n.addResourceBundle(locale, "translation", resource, true, true)
    loadedLocales.add(locale)
}

let latestRequest = 0

/** Apply a preference: load its catalog, switch i18next, and keep `<html lang>` in step. */
export async function applyLanguagePreference(
    preference: LanguagePreference,
    {persist = true}: {persist?: boolean} = {},
) {
    const request = ++latestRequest
    const locale = resolveLocale(preference)
    if (persist) writeLanguagePreference(preference)
    await ensureLocale(locale)
    // A slower load must not override a later choice.
    if (request !== latestRequest) return
    await i18n.changeLanguage(locale)
    if (typeof document !== "undefined") document.documentElement.lang = locale
}

/** Translate outside React (event handlers, toasts). Inside components use `useLocalize()`. */
export const localize = (key: string, options?: Record<string, unknown>) =>
    i18n.t(key, options) as string

/** Test hook: forget loaded catalogs and return to English. */
export async function __resetI18nForTests() {
    latestRequest = 0
    for (const locale of supportedLocales) {
        if (locale !== "en") i18n.removeResourceBundle(locale, "translation")
    }
    loadedLocales.clear()
    loadedLocales.add("en")
    await i18n.changeLanguage("en")
}
